// npm
import React from 'react';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { useMutation } from 'react-query';

// common
import { IMemberProps } from 'common/interface/props';
import { random } from 'common/common.function';
import { IBizRegNoOutput, ICreateAccountOutput, IEmailCheckOutput } from 'components/form/interfaces/member.interface';
import { checkingBizNo, COUPANG, regexEmail, regexPassword, regexTel } from 'common/common.constatns';

// atomic component
import Grid from 'components/UI/atoms/Grid';
import TextField from 'components/UI/atoms/TextField';
import Button from 'components/UI/atoms/Button';
import FormWrap from 'components/UI/molecules/FormWrap';
import InputWrap from 'components/UI/molecules/InputWrap';

// etc
import { FormError } from 'components/form/form-error';
import CheckBox from 'components/UI/atoms/CheckBox';
import { useSelector } from 'react-redux';
import { RootState } from 'redux_modules';
import { BizNoCheckMutation, EmailCheckMutation, CreateAccountMutation } from 'components/form/api/member.api';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function CreateAccount({ solType }: IMemberProps) {
  const {
    register,
    watch,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { agreeInfo } = useSelector((state: RootState) => ({
    agreeInfo: state.agreeInfo,
  }));

  // 진행 스텝 상태
  const [step, setStep] = React.useState({
    email: false,
    authCode: false,
    bizRegNo: false,
  });

  const navigate = useNavigate(); // 히스토리 구하기
  const [searchParams] = useSearchParams(); // url param 가져오기

  const password: any = React.useRef(); //회원가입 전용, 비밀번호와 비밀번호 체크 일치여부 확인시 필요
  password.current = watch('password'); //비밀번호에 입력한 값

  React.useEffect(() => {
    const sendAddressSeq = searchParams.get('sendAddressSeq');
    const returnAddressSeq = searchParams.get('returnAddressSeq');

    if (sendAddressSeq !== null && returnAddressSeq !== null) {
      sessionStorage.setItem('addressSeq', JSON.stringify({ sendAddressSeq: sendAddressSeq, returnAddressSeq: returnAddressSeq }));
    }
  }, []);

  const { mutate: emailCheck } = useMutation(EmailCheckMutation, {
    // mutate 함수가 실행되기 전에 실행
    onMutate: (variables) => {
      // 무한 로딩 화면 생성
      Swal.fire({
        title: `회원가입 중...`,
        html: '인증메일 발송중...', // add html attribute if you want or remove
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });
    },
    onSuccess: async (data: IEmailCheckOutput, variables) => {
      await Swal.fire({
        title: '성공',
        html: `사용 가능한 이메일 주소 입니다. 인증을 위한 메일이 발송되었으니 메일을 확인해 주세요.`,
        icon: 'success',
        confirmButtonText: '확인',
      });

      setStep({
        email: true,
        authCode: false,
        bizRegNo: false,
      });
    },
    onError: (error: any) => {
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    },
  });

  const { mutate: bizNoCheck } = useMutation(BizNoCheckMutation, {
    onMutate: (variable) => {},
    onSuccess: async (data: IBizRegNoOutput, variable) => {
      await Swal.fire({
        title: '성공',
        html: `사업자번호가 확인되었습니다. 회원가입을 진행해주세요`,
        icon: 'success',
        confirmButtonText: '확인',
      });
      setStep({
        email: true,
        authCode: true,
        bizRegNo: true,
      });
    },
    onError: (error: any) => {
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    },
  });

  const { mutate: createAccount } = useMutation(CreateAccountMutation, {
    onMutate: (variables) => {},
    onSuccess: async (data: ICreateAccountOutput, variables) => {
      await Swal.fire({
        title: '성공',
        html: `회원가입 성공`,
        icon: 'success',
        confirmButtonText: '확인',
      });

      //인증코드는 사용했으니 제거함
      sessionStorage.removeItem('emailAuthCode');

      //자바스크립트에서 쿠키에 있는 jwt 접근이 안되니깐(httponly설정) - 일부로막음, 메일주소 세션스토리지에 넣음
      sessionStorage.setItem(
        'userInfo',
        JSON.stringify({
          email: variables.email,
          solType: variables.solType,
        }),
      );

      navigate(`/${variables.solType.toLowerCase()}/job/shop-login`); //해당 주소로 이동
      window.scrollTo(0, 0); //스크롤 최상단으로 올리기
    },
    onError: (error: any) => {
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    },
  });

  //step1 - 이메일 중복조회 및 인증
  async function onEmailCheck() {
    const { email } = getValues();
    const authCode = random(8);
    sessionStorage.setItem('emailAuthCode', authCode.toString());
    emailCheck({ email, authCode });
  }

  //step2 - 인증번호 확인
  async function onConfirmCodeCheck() {
    const { confirmCode } = getValues();
    try {
      const authCode = sessionStorage.getItem('emailAuthCode');
      if (confirmCode.toString() !== authCode) {
        await Swal.fire({
          title: '실패',
          html: `인증번호가 일치하지 않습니다.`,
          icon: 'error',
          confirmButtonText: '확인',
        });
      } else {
        await Swal.fire({
          title: '성공',
          html: `인증번호가 확인되었습니다. 회원가입을 진행해주세요`,
          icon: 'success',
          confirmButtonText: '확인',
        });
        solType !== COUPANG
          ? setStep({
              email: true,
              authCode: true,
              bizRegNo: false,
            })
          : setStep({
              email: true,
              authCode: true,
              bizRegNo: true,
            });
      }
    } catch (e: any) {
      Swal.fire({
        title: '실패',
        html: `${e.message}`,
        icon: 'error',
      });
    }
  }

  // Step3- 사업자번호 확인
  async function onBizNoCheck() {
    let { bizRegNo } = getValues();
    bizRegNo = parseInt(bizRegNo);
    //쿠팡 제외하고는 모두 사업제 체크
    if (solType !== COUPANG) {
      bizNoCheck({ bizRegNo, solType });
    }
  }

  //step3 회원가입
  //() -> react에서는 e(Event)를 받지만 react-hook-form에서는 data를 받음
  async function onSubmit() {
    try {
      if (step.email && step.authCode && step.bizRegNo && agreeInfo.status === 'SUCCESS') {
        let { email, bizRegNo, password, tel, smsAgree, mailAgree } = getValues();
        bizRegNo = parseInt(bizRegNo);

        // sms, 이메일 수신 동의 하나라도 미체크 시
        if (smsAgree === false || mailAgree === false) {
          const result = await Swal.fire({
            title: 'SMS 및 이메일 알림 동의',
            html: `SMS 및 이메일 알림을 동의하지 않으셨습니다. <br />
                      동의하지 않을 경우 <b>상품등록 완료 알림</b>을 받으실 수 없습니다.
                      <strong>SMS 및 이메일 알림에 동의 하시겠습니까?</strong>`,
            showCancelButton: true,
            cancelButtonText: '취소',
            confirmButtonText: '동의',
          });

          if (result.value === true) {
            smsAgree = true;
            mailAgree = true;
          }
        }
        createAccount({ solType, email, bizRegNo, password, tel, smsAgree, mailAgree });
      } else {
        if (!step.email) {
          await Swal.fire({
            title: '실패',
            html: `이메일 중복 조회를 먼저 진행해 주시기 바랍니다.`,
            icon: 'warning',
            confirmButtonText: '확인',
          });
        }

        if (step.email && !step.authCode) {
          await Swal.fire({
            title: '실패',
            html: `이메일 인증번호를 입력해 주시기 바랍니다.`,
            icon: 'error',
            confirmButtonText: '확인',
          });
        }
        if (solType !== COUPANG && step.email && step.authCode && !step.bizRegNo) {
          await Swal.fire({
            title: '실패',
            html: `사업자 번호를 입력해 주시기 바랍니다.`,
            icon: 'error',
            confirmButtonText: '확인',
          });
        }
        if (agreeInfo.status !== 'SUCCESS') {
          await Swal.fire({
            title: '실패',
            html: `이용약관에 동의해주시기 바랍니다.`,
            icon: 'error',
            confirmButtonText: '확인',
          });
        }
      }
    } catch (e: any) {
      Swal.fire({
        title: '실패',
        html: `${e.message}`,
        icon: 'error',
      });
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormWrap>
          <FormWrap.Top>
            <Grid top={50}>
              <InputWrap
                label="이메일 ID"
                labelWidth={140}
                input={
                  <TextField
                    type="email"
                    placeholder="이메일 형식으로 입력해 주세요."
                    {...{ ...register(`email`, { required: true, maxLength: 50, pattern: regexEmail }) }}
                  />
                }
                adornment={
                  <Button width={140} size="small" color="main" variant="outlined" onClick={handleSubmit(onEmailCheck)}>
                    중복조회 및 인증
                  </Button>
                }
                error={
                  errors?.email && errors.email.type === 'required' ? (
                    <FormError errorMessage={'이메일은 필수 입력 항목입니다.'} />
                  ) : errors?.email && errors.email.type === 'maxLength' ? (
                    <FormError errorMessage={'이메일의 최대 길이를 초과하였습니다.(최대 50자)'} />
                  ) : errors?.email && errors.email.type === 'pattern' ? (
                    <FormError errorMessage={'올바른 이메일 형식이 아닙니다.'} />
                  ) : (
                    <></>
                  )
                }
              />
            </Grid>

            {step.email && (
              <Grid top={50}>
                <InputWrap
                  label="인증번호"
                  labelWidth={140}
                  input={
                    <TextField
                      type="text"
                      placeholder="인증번호를 입력해 주세요."
                      {...register(`confirmCode`, { required: step.email, pattern: regexTel, valueAsNumber: true, minLength: 8, maxLength: 8 })}
                    />
                  }
                  error={
                    errors?.confirmCode && errors.confirmCode.type === 'required' ? (
                      <FormError errorMessage={'인증번호는 필수 입력 항목입니다.'} />
                    ) : errors?.confirmCode && errors.confirmCode.type === 'minLength' ? (
                      <FormError errorMessage={'인증번호는 발급받은 8자리로만 입력해 주세요'} />
                    ) : errors?.confirmCode && errors.confirmCode.type === 'maxLength' ? (
                      <FormError errorMessage={'인증번호는 발급받은 8자리로만 입력해 주세요'} />
                    ) : errors?.confirmCode && errors.confirmCode.type === 'pattern' ? (
                      <FormError errorMessage={'인증번호는 숫자만 입력해 주세요'} />
                    ) : (
                      <></>
                    )
                  }
                  adornment={
                    <Button width={140} size="small" variant="outlined" color="main" onClick={handleSubmit(onConfirmCodeCheck)}>
                      인증번호 확인
                    </Button>
                  }
                />
              </Grid>
            )}
            {solType !== COUPANG && (
              <Grid top={50}>
                <InputWrap
                  label="사업자 번호"
                  labelWidth={140}
                  input={
                    <TextField
                      type="text"
                      placeholder="숫자만 입력해 주세요."
                      {...{
                        ...(step.email &&
                          step.authCode &&
                          register(`bizRegNo`, { required: true, pattern: regexTel, validate: (value) => checkingBizNo(value) })),
                      }}
                    />
                  }
                  adornment={
                    <Button width={140} size="small" color="main" variant="outlined" onClick={handleSubmit(onBizNoCheck)}>
                      사업자 번호 조회
                    </Button>
                  }
                  error={
                    errors?.bizRegNo && errors.bizRegNo.type === 'required' ? (
                      <FormError errorMessage={'사업자 번호는 필수 입력 항목입니다.'} />
                    ) : errors?.bizRegNo && errors.bizRegNo.type === 'pattern' ? (
                      <FormError errorMessage={'사업자 번호는 숫자만 입력해 주세요.'} />
                    ) : errors?.bizRegNo && errors.bizRegNo.type === 'validate' ? (
                      <FormError errorMessage={'유효하지 않은 사업자번호 입니다.'} />
                    ) : (
                      <></>
                    )
                  }
                />
              </Grid>
            )}

            <Grid top={50}>
              <InputWrap
                label="비밀번호"
                labelWidth={140}
                input={
                  <TextField
                    type="password"
                    placeholder="영문 + 숫자 + 특수 문자 조합으로 8자 이상 입력해주세요."
                    {...{
                      ...(step.email &&
                        step.authCode &&
                        step.bizRegNo &&
                        register(`password`, { required: step.authCode, minLength: 8, pattern: regexPassword })),
                    }}
                  />
                }
                error={
                  errors?.password && errors.password.type === 'required' ? (
                    <FormError errorMessage={'비밀번호는 필수 입력 항목입니다.'} />
                  ) : errors?.password && errors.password.type === 'minLength' ? (
                    <FormError errorMessage={'비밀번호는 8자 이상 입력해 주세요.'} />
                  ) : errors?.password && errors.password.type === 'pattern' ? (
                    <FormError errorMessage={'비밀번호는 영문+숫자+특문 8자 이상 입력해 주세요.'} />
                  ) : (
                    <></>
                  )
                }
              />
            </Grid>

            <Grid top={50}>
              <InputWrap
                label="비밀번호확인"
                labelWidth={140}
                input={
                  <TextField
                    type="password"
                    placeholder="비밀번호를 한번 더 입력해 주세요."
                    {...(step.email &&
                      step.authCode &&
                      step.bizRegNo && {
                        ...register(`confirmPassword`, {
                          required: step.authCode,
                          minLength: 8,
                          pattern: regexPassword,
                          validate: (value) => value === password.current,
                        }),
                      })}
                  />
                }
                error={
                  errors?.confirmPassword && errors.confirmPassword.type === 'required' ? (
                    <FormError errorMessage={'비밀번호체크는 필수 입력 항목입니다.'} />
                  ) : errors?.confirmPassword && errors.confirmPassword.type === 'minLength' ? (
                    <FormError errorMessage={'비밀번호는 8자 이상 입력해 주세요.'} />
                  ) : errors?.confirmPassword && errors.confirmPassword.type === 'pattern' ? (
                    <FormError errorMessage={'비밀번호는 영문+숫자+특문 8자 이상 입력해 주세요.'} />
                  ) : errors?.confirmPassword && errors.confirmPassword.type === 'validate' && step.authCode ? (
                    <FormError errorMessage={'비밀번호와 일치하지 않습니다.'} />
                  ) : (
                    <></>
                  )
                }
              />
            </Grid>

            <Grid top={50}>
              <InputWrap
                label="휴대폰번호"
                labelWidth={140}
                input={
                  <TextField
                    type="text"
                    placeholder="(-)을 제외한 숫자만 입력해 주세요."
                    {...{
                      ...(step.email &&
                        step.authCode &&
                        step.bizRegNo &&
                        register(`tel`, {
                          required: step.authCode,
                          pattern: regexTel,
                          // valueAsNumber: true,
                        })),
                    }}
                  />
                }
                error={
                  errors?.tel && errors.tel.type === 'required' ? (
                    <FormError errorMessage={'휴대폰 번호는 필수 입력 항목입니다.'} />
                  ) : errors?.tel && errors.tel.type === 'pattern' ? (
                    <FormError errorMessage={'휴대폰 번호는 숫자만 입력해 주세요.'} />
                  ) : (
                    <></>
                  )
                }
              />
            </Grid>

            <Grid top={50}>
              <InputWrap
                label="알림수신동의"
                labelWidth={140}
                input={
                  <>
                    <Grid>
                      <CheckBox label="SMS 동의" {...register('smsAgree')} defaultChecked={true} />
                      <CheckBox label="이메일 동의" {...register('mailAgree')} defaultChecked={true} />
                    </Grid>
                    <p style={{ fontSize: '0.77rem', letterSpacing: '-1px', color: '#565656', marginTop: '7px' }}>
                      * 상품 이전 완료 시 알림 및 프로모션 등 플레이오토에서 제공하는 알림 수신에 동의합니다.
                    </p>
                  </>
                }
              />
            </Grid>
          </FormWrap.Top>
          <FormWrap.Bottom>
            <Grid display="flex" justifyContent="center">
              <Button color="main" variant="contained" size="medium" width={240}>
                다음
              </Button>
            </Grid>
          </FormWrap.Bottom>
        </FormWrap>
      </form>
    </>
  );
}
